import React, { useRef, useEffect, useState } from 'react';

import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { GalleryItem } from '../Career/types';
import DOMPurify from "dompurify";
// components
import { Post2 } from 'components/blog';
import ContactForm from './ContactPost';
import { Helmet } from 'react-helmet-async';

type PostContentProps = {
    gallery: GalleryItem[];
    post: Post2 | null;
};

const PostContent = ({ gallery, post }: PostContentProps) => {

    const splitDescription = (htmlContent: string) => {
        if (!htmlContent) return { firstPart: '', secondPart: '' };

        const paragraphs = htmlContent.split('</p>');

        const splitIndex = Math.min(4, paragraphs.length - 1);
        const firstPart = paragraphs.slice(0, splitIndex).join('</p>') + '</p>';
        const secondPart = paragraphs.slice(splitIndex).join('</p>');

        return { firstPart, secondPart };
    };

    const { firstPart, secondPart } = splitDescription(post?.longDescription || '');


    const sanitizeAndFixHeadings = (html: string) => {
        let clean = DOMPurify.sanitize(html);
        clean = clean.replace(/<h1>/g, "<h2>").replace(/<\/h1>/g, "</h2>");
        return clean;
    };

    const sanitizedFirstPart = sanitizeAndFixHeadings(firstPart);
    const sanitizedSecondPart = sanitizeAndFixHeadings(secondPart);


    // const sanitizedFirstPart = DOMPurify.sanitize(firstPart);
    // const sanitizedSecondPart = DOMPurify.sanitize(secondPart);

    const leftColRef = useRef<HTMLDivElement>(null);
    const rightColRef = useRef<HTMLDivElement>(null);
    const rightInnerRef = useRef<HTMLDivElement>(null);
    const [stackBelow, setStackBelow] = useState(false);
    const [leftTaller, setLeftTaller] = useState(false);
    const [formCompleted, setFormCompleted] = useState(false);
    const [collapseRight, setCollapseRight] = useState(false);

    const handleFormFinish = () => {
        // start collapse animation of right column, then mark form completed
        setCollapseRight(true);
    };

    // when collapse animation ends, mark form as completed to let blog expand
    useEffect(() => {
        if (!collapseRight) return;
        const el = rightInnerRef.current || rightColRef.current;
        if (!el) {
            setFormCompleted(true);
            return;
        }

        const onTransitionEnd = (e: TransitionEvent) => {
            if ((e as any).propertyName === 'max-height' || (e as any).propertyName === 'height') {
                setFormCompleted(true);
                setCollapseRight(false);
            }
        };

        el.addEventListener('transitionend', onTransitionEnd as any);
        // safety timeout in case transitionend doesn't fire
        const t = setTimeout(() => {
            setFormCompleted(true);
            setCollapseRight(false);
        }, 700);

        return () => {
            clearTimeout(t);
            el.removeEventListener('transitionend', onTransitionEnd as any);
        };
    }, [collapseRight]);

    // measure heights and decide whether to render blog data in its own full-width row
    useEffect(() => {
        if (formCompleted) {
            // when form is gone we don't need to measure
            setStackBelow(false);
            return;
        }

        const measure = () => {
            const leftH = leftColRef.current?.offsetHeight || 0;
            const rightH = rightColRef.current?.offsetHeight || 0;
            setStackBelow(rightH > leftH);
            // add small threshold to avoid flicker
            setLeftTaller(leftH > rightH + 80);
        };

        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [post, firstPart, secondPart, formCompleted]);

    if (!post) {
        return (
            <Container className="text-center py-5">
                <Spinner animation="border" variant="primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    return (

        <>
            <Helmet>
                <title>{`${post?.title} | Sales Trackify CRM & Sales Management`}</title>
                <meta name="description" content={`${post?.shortDescription}`} />

                <meta name="keywords" content={post?.metaKeywords || `${post?.blogTags}`} />

                <meta property="og:title" content={post?.title} />
                <meta property="og:description" content={post?.shortDescription} />
                <meta property="og:image" content={post?.blogImage} />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={post?.title} />
                <meta name="twitter:description" content={post?.shortDescription} />
                <meta name="twitter:image" content={post?.blogImage} />
            </Helmet>

            <section className="position-relative pb-5">
                <Container fluid={formCompleted}>
                    <Row className="mb-5" style={{ alignItems: 'flex-start' }}>
                        <Col
                            lg={formCompleted ? 12 : 7}
                            ref={leftColRef}
                            className={formCompleted ? 'blog-fullwidth' : ''}
                        >

                            <div className="text-black mb-4 fs-17 mt-4">
                                {post?.shortDescription}
                            </div>

                            {(!stackBelow || formCompleted) && post.blogImage && (
                                <figure className="figure text-center mb-4 mt-4">
                                    <img
                                        src={post.blogImage}
                                        alt="Blog"
                                        className="figure-img img-fluid rounded shadow"
                                        style={{
                                            maxWidth: '100%',
                                            height: 'auto',
                                            maxHeight: '400px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </figure>
                            )}

                            <div className={formCompleted ? 'two-column-content' : ''}>
                                {firstPart && (
                                    <div className="mb-4">
                                        <div
                                            dangerouslySetInnerHTML={{ __html: sanitizedFirstPart }}
                                        />
                                    </div>
                                )}

                                {/* render image and extra text inline if the right column isn't taller or if form is gone */}

                                {(!stackBelow || formCompleted) && secondPart && (
                                    <div className="mt-4">
                                        <div
                                            dangerouslySetInnerHTML={{ __html: sanitizedSecondPart }}
                                        />
                                    </div>
                                )}
                            </div>
                        </Col>

                        {!formCompleted && (
                            <Col lg={5} ref={rightColRef}>
                                {/* <div ref={rightInnerRef} 
                                className={collapseRight ? 'collapsing-right closing border-wrapper' : 'collapsing-right border-wrapper'} style={{ top: '100px' }}> */}
                                {/* <div
                                    ref={rightInnerRef}
                                    className={
                                        collapseRight
                                            ? 'collapsing-right closing border-wrapper sticky-form'
                                            : 'collapsing-right border-wrapper sticky-form'
                                    }
                                >
                                    <div className="border border-primary">
                                        <ContactForm />
                                    </div>
                                </div> */}
                                <div className="sticky-form">
                                    <div
                                        ref={rightInnerRef}
                                        className={
                                            collapseRight
                                                ? 'collapsing-right closing border-wrapper'
                                                : 'collapsing-right border-wrapper'
                                        }
                                    >
                                        <div className="border border-primary">
                                            <ContactForm />
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        )}
                    </Row>

                    {/* if contact form is taller, push remaining content to full-width rows below */}
                    {stackBelow && post.blogImage && (
                        <Row className="mb-5">
                            <Col lg={12}>
                                <figure className="figure text-center mb-4">
                                    <img
                                        src={post.blogImage}
                                        alt="Blog"
                                        className="figure-img img-fluid rounded shadow"
                                        style={{
                                            maxWidth: '100%',
                                            height: 'auto',
                                            maxHeight: '400px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </figure>
                            </Col>
                        </Row>
                    )}

                    {stackBelow && secondPart && (
                        <Row>
                            <Col lg={12}>
                                <div className="mt-4">
                                    <div
                                        dangerouslySetInnerHTML={{ __html: sanitizedSecondPart }}
                                    />
                                </div>
                            </Col>
                        </Row>
                    )}
                </Container>
            </section>

            {/* extra styles for post layout adjustments */}
            <style>{`
                /* when the form is complete we allow content to flow in two columns */
                .two-column-content {
                    column-count: 2;
                    column-gap: 2rem;
                }

                /* optionally remove any max-width imposed by container when fluid */
                .blog-fullwidth {
                    width: 100%;
                }
              

                /* collapsing right column animation (applies to inner wrapper) */
                .border-wrapper {
                    display: block;
                }

                .collapsing-right {
                    max-height: 2000px;
                    // overflow: hidden;
                    transition: max-height 0.45s ease, opacity 0.35s ease, padding 0.35s ease;
                    opacity: 1;
                }
      .sticky-form {
                  position: sticky;
                      top: 100px; /* adjust as needed */
                 height: fit-content;
                    }
                .collapsing-right.closing {
                    max-height: 0 !important;
                    opacity: 0;
                    padding-top: 0 !important;
                    padding-bottom: 0 !important;
                }
            `}</style>

        </>
    );
};

export default PostContent;